import { CreditLedger, pauseStateOn } from '@/domain/credit';
import { addWeeks, compareDates } from '@/domain/dates';
import type { Assignment, IsoDate, Settings, Volunteer } from '@/shared/types';

/**
 * Derived views for the reporting tab (spec 6 and 8).
 *
 * These exist so the point system can be sanity-checked against reality. If
 * the math over-optimises, it shows up here rather than having to be debugged
 * blind. Bail counts are surfaced for the coordinator's judgement in a
 * check-in conversation — they are deliberately kept out of the ranking math.
 */

export interface VolunteerStats {
  volunteerId: string;
  name: string;
  status: Volunteer['status'];
  eligibleToday: boolean;
  /** Effective credit as of today: what the ranker actually sorts on. */
  credit: number;
  /** Rides completed inside the reporting window. */
  completed: number;
  /** Times the rider canceled after they had committed. */
  riderCanceled: number;
  /** Times they bailed. Tracked, never scored. */
  bailed: number;
  bailDates: IsoDate[];
  /** Assigned, still Pending, dated today or later. */
  upcoming: number;
  lastAssigned: IsoDate | null;
}

export interface ReportOptions {
  /** How far back the completed/bail/cancel counts look. Default 13 weeks. */
  reportingWindowWeeks?: number;
}

export function computeVolunteerStats(
  volunteers: readonly Volunteer[],
  assignments: readonly Assignment[],
  settings: Settings,
  today: IsoDate,
  options: ReportOptions = {},
): VolunteerStats[] {
  const { reportingWindowWeeks = 13 } = options;
  const reportStart = addWeeks(today, -reportingWindowWeeks);
  const ledger = CreditLedger.from(volunteers, assignments, settings.rollingWindowWeeks);

  const byVolunteer = new Map<string, Assignment[]>();
  for (const assignment of assignments) {
    if (!assignment.volunteerId) continue;
    const list = byVolunteer.get(assignment.volunteerId);
    if (list) list.push(assignment);
    else byVolunteer.set(assignment.volunteerId, [assignment]);
  }

  return volunteers.map((volunteer) => {
    const rows = byVolunteer.get(volunteer.id) ?? [];

    let completed = 0;
    let riderCanceled = 0;
    let bailed = 0;
    let upcoming = 0;
    const bailDates: IsoDate[] = [];

    for (const row of rows) {
      const inWindow =
        compareDates(row.date, reportStart) >= 0 && compareDates(row.date, today) <= 0;

      if (inWindow) {
        if (row.outcome === 'Completed') completed++;
        else if (row.outcome === 'Rider Canceled') riderCanceled++;
        else if (row.outcome === 'Volunteer Bailed') {
          bailed++;
          bailDates.push(row.date);
        }
      }

      if (row.outcome === 'Pending' && compareDates(row.date, today) >= 0) upcoming++;
    }

    bailDates.sort(compareDates);

    return {
      volunteerId: volunteer.id,
      name: volunteer.name,
      status: volunteer.status,
      eligibleToday: pauseStateOn(volunteer, today) === 'active',
      credit: ledger.creditAt(volunteer.id, today),
      completed,
      riderCanceled,
      bailed,
      bailDates,
      upcoming,
      lastAssigned: ledger.lastAssignedBefore(volunteer.id, today),
    };
  });
}

export interface CoverageSummary {
  total: number;
  assigned: number;
  unassigned: number;
  confirmed: number;
  /** Dates with nobody on them, soonest first. */
  gaps: IsoDate[];
}

/** How well the next stretch of the calendar is covered. */
export function computeCoverage(
  assignments: readonly Assignment[],
  from: IsoDate,
  to: IsoDate,
): CoverageSummary {
  const summary: CoverageSummary = {
    total: 0,
    assigned: 0,
    unassigned: 0,
    confirmed: 0,
    gaps: [],
  };

  for (const assignment of assignments) {
    if (compareDates(assignment.date, from) < 0 || compareDates(assignment.date, to) > 0) continue;
    if (assignment.outcome === 'No Meeting') continue;

    summary.total++;
    if (assignment.volunteerId) {
      summary.assigned++;
      if (assignment.confirmed) summary.confirmed++;
    } else {
      summary.unassigned++;
      summary.gaps.push(assignment.date);
    }
  }

  summary.gaps.sort(compareDates);
  return summary;
}
